import useAuth from '@/composables/useAuth';
import { useSelectedWarehouse } from '@/composables/useSelectedWarehouse';
import type { WarehouseDetails } from '@satellite/composables/useWarehousesPresenterV2';
import { useAnalytics } from '@satellite/composables/useAnalytics';
import type { TrackOptions } from '@satellite/analytics/AnalyticsClient';
import type { TaskViewData } from '@nova/core';
import {
  Events,
  type HeliosEventsMap,
  type ReadyForPickupNotificationSentPayload,
  type ReadyForPickupNotificationSkippedPayload,
  type YardAssetAssignedToSpotPayload,
  type YardAssetContainerAppointmentLinkedPayload,
  type YardAssetContainerAppointmentUnlinkedPayload,
  type YardAssetContainerCreatedPayload,
  type YardAssetContainerDroppedPayload,
  type YardAssetContainerHookedPayload,
  type YardAssetContainerStatusUpdatedPayload,
  type YardAssetContainerUpdatedPayload,
  type YardAssetReassignedToSpotPayload,
  type YardAssetVisitAppointmentLinkedPayload,
  type YardAssetVisitAppointmentUnlinkedPayload,
  type YardAssetVisitCreatedPayload,
  type YardAssetVisitDepartedPayload,
  type YardAssetsExportedPayload,
  type YardEventBasePayload,
  type YardGoToSpotClickedPayload,
  type YardTaskPayload
} from '@/analytics/events';
import { buildYardTaskAnalyticsEventContext } from '@/analytics/yardTaskAnalyticsEventContext';

interface YardSpot {
  id?: string;
  code?: string;
  type?: string;
  spotAreaId?: string;
}

interface YardAsset {
  id?: string;
  code?: string;
  type?: string;
  visitType?: string;
  isPlanned?: boolean;
  /** Set explicitly by callers (e.g. drag-and-drop payloads) that already know the asset kind and
   * can't rely on the heuristic below (their asset shape may not include `code`). */
  isContainer?: boolean;
}

interface YardEventContext {
  spot?: YardSpot | null;
  /** An asset visit. If the tracked subject is actually a container, prefer `assetContainer` below
   * instead of relying on the isContainer heuristic/flag. */
  assetVisit?: YardAsset | null;
  /** Explicit asset container. Takes priority over `assetVisit` so callers don't have to rely on
   * the isAssetContainer() heuristic (or an isContainer flag) to disambiguate. */
  assetContainer?: YardAsset | null;
  entryPoint?: string | null;
}

const isAssetContainer = (asset?: YardAsset | null) => {
  if (!asset) {
    return false;
  }
  if (typeof asset.isContainer === 'boolean') {
    return asset.isContainer;
  }
  return typeof asset.code === 'string' && Boolean(asset.type) && !('visitType' in asset);
};

/**
 * Some events (e.g. dropping/hooking a specific container out of a multi-container asset visit)
 * need to override the base-derived asset_container_id/type with a more specific value, mirroring
 * the legacy tracker's `...metadata` spread taking priority over the auto-derived eventData.
 */
type WithAssetContainerOverride<T> = Omit<T, keyof YardEventBasePayload> &
  Pick<YardEventBasePayload, 'asset_container_id' | 'asset_container_type'>;

export function useYardAnalytics() {
  const analytics = useAnalytics<HeliosEventsMap>();
  const { meRef } = useAuth();
  const { selectedWarehouse } = useSelectedWarehouse();

  function buildBasePayload(context: YardEventContext = {}): YardEventBasePayload {
    const { spot, assetVisit, entryPoint } = context;
    const me = meRef.value;
    // `selectedWarehouse` type-checks as a nested Ref due to a pre-existing storeToRefs/useLocalStorageRef
    // typing quirk in useWarehouseStore; the runtime value is always a plain WarehouseDetails | null.
    const warehouse = selectedWarehouse.value as unknown as WarehouseDetails | null;
    // Prefer an explicit `assetContainer` so callers don't have to rely on the isContainer
    // heuristic/flag to disambiguate; fall back to detecting it off `assetVisit` for callers that
    // haven't been updated to pass it explicitly yet.
    const container = context.assetContainer ?? (isAssetContainer(assetVisit) ? assetVisit : null);
    const visit = container ? null : assetVisit;

    return {
      org_id: me?.orgId ?? null,
      org_name: me?.orgName ?? null,
      warehouse_id: warehouse?.id ?? null,
      warehouse_name: warehouse?.name ?? null,
      area_id: spot?.spotAreaId ?? null,
      spot_id: spot?.id ?? null,
      spot_code: spot?.code ?? null,
      spot_type: spot?.type ?? null,
      asset_visit_id: visit?.id ?? null,
      asset_visit_type: visit?.visitType ?? null,
      asset_container_id: container?.id ?? null,
      asset_container_type: container?.type ?? null,
      checkin_type: assetVisit
        ? assetVisit.isPlanned
          ? 'Planned Arrival'
          : 'Unplanned Arrival'
        : null,
      entry_point: entryPoint ?? null
    };
  }

  function trackYardAssetVisitCreated(
    context: YardEventContext,
    extra: Omit<YardAssetVisitCreatedPayload, keyof YardEventBasePayload>,
    options: TrackOptions<YardAssetVisitCreatedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_VISIT_CREATED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetContainerCreated(
    context: YardEventContext,
    extra: Omit<YardAssetContainerCreatedPayload, keyof YardEventBasePayload>,
    options: TrackOptions<YardAssetContainerCreatedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_CONTAINER_CREATED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetContainerStatusUpdated(
    context: YardEventContext,
    extra: Omit<YardAssetContainerStatusUpdatedPayload, keyof YardEventBasePayload>,
    options: TrackOptions<YardAssetContainerStatusUpdatedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_CONTAINER_STATUS_UPDATED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetContainerUpdated(
    context: YardEventContext,
    extra: Omit<YardAssetContainerUpdatedPayload, keyof YardEventBasePayload> = {},
    options: TrackOptions<YardAssetContainerUpdatedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_CONTAINER_UPDATED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetVisitAppointmentLinked(
    context: YardEventContext,
    extra: Omit<YardAssetVisitAppointmentLinkedPayload, keyof YardEventBasePayload>,
    options: TrackOptions<YardAssetVisitAppointmentLinkedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_VISIT_APPOINTMENT_LINKED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetVisitAppointmentUnlinked(
    context: YardEventContext,
    options: TrackOptions<YardAssetVisitAppointmentUnlinkedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_VISIT_APPOINTMENT_UNLINKED, {
      ...buildBasePayload(context),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetContainerAppointmentLinked(
    context: YardEventContext,
    extra: WithAssetContainerOverride<YardAssetContainerAppointmentLinkedPayload>,
    options: TrackOptions<YardAssetContainerAppointmentLinkedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_CONTAINER_APPOINTMENT_LINKED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetContainerAppointmentUnlinked(
    context: YardEventContext,
    extra: WithAssetContainerOverride<YardAssetContainerAppointmentUnlinkedPayload>,
    options: TrackOptions<YardAssetContainerAppointmentUnlinkedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_CONTAINER_APPOINTMENT_UNLINKED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardGoToSpotClicked(
    context: YardEventContext,
    options: TrackOptions<YardGoToSpotClickedPayload> = {}
  ) {
    analytics.track(Events.YARD.GO_TO_SPOT_CLICKED, {
      ...buildBasePayload(context),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetContainerDropped(
    context: YardEventContext,
    extra: WithAssetContainerOverride<YardAssetContainerDroppedPayload>,
    options: TrackOptions<YardAssetContainerDroppedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_CONTAINER_DROPPED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetContainerHooked(
    context: YardEventContext,
    extra: WithAssetContainerOverride<YardAssetContainerHookedPayload>,
    options: TrackOptions<YardAssetContainerHookedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_CONTAINER_HOOKED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetVisitDeparted(
    context: YardEventContext,
    extra: Omit<YardAssetVisitDepartedPayload, keyof YardEventBasePayload>,
    options: TrackOptions<YardAssetVisitDepartedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_VISIT_DEPARTED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetAssignedToSpot(
    context: YardEventContext,
    options: TrackOptions<YardAssetAssignedToSpotPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_ASSIGNED_TO_SPOT, {
      ...buildBasePayload(context),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetReassignedToSpot(
    context: YardEventContext,
    extra: Omit<YardAssetReassignedToSpotPayload, keyof YardEventBasePayload> = {},
    options: TrackOptions<YardAssetReassignedToSpotPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSET_REASSIGNED_TO_SPOT, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardAssetsExported(
    context: YardEventContext,
    extra: Omit<YardAssetsExportedPayload, keyof YardEventBasePayload>,
    options: TrackOptions<YardAssetsExportedPayload> = {}
  ) {
    analytics.track(Events.YARD.ASSETS_EXPORTED, {
      ...buildBasePayload(context),
      ...extra,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackReadyForPickupNotificationSent(
    payload: ReadyForPickupNotificationSentPayload,
    options: TrackOptions<ReadyForPickupNotificationSentPayload> = {}
  ) {
    analytics.track(Events.READY_FOR_PICKUP.NOTIFICATION_SENT, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackReadyForPickupNotificationSkipped(
    payload: ReadyForPickupNotificationSkippedPayload,
    options: TrackOptions<ReadyForPickupNotificationSkippedPayload> = {}
  ) {
    analytics.track(Events.READY_FOR_PICKUP.NOTIFICATION_SKIPPED, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardTaskCreated(
    task: TaskViewData,
    entryPoint: string,
    options: TrackOptions<YardTaskPayload> = {}
  ) {
    analytics.track(Events.TASKS.CREATED, {
      ...buildYardTaskAnalyticsEventContext(task, { entryPoint }),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackYardTaskUpdated(
    task: TaskViewData,
    entryPoint: string,
    options: TrackOptions<YardTaskPayload> = {}
  ) {
    analytics.track(Events.TASKS.UPDATED, {
      ...buildYardTaskAnalyticsEventContext(task, { entryPoint }),
      ...(options.additionalPayload ?? {})
    });
  }

  return {
    trackYardAssetVisitCreated,
    trackYardAssetContainerCreated,
    trackYardAssetContainerStatusUpdated,
    trackYardAssetContainerUpdated,
    trackYardAssetVisitAppointmentLinked,
    trackYardAssetVisitAppointmentUnlinked,
    trackYardAssetContainerAppointmentLinked,
    trackYardAssetContainerAppointmentUnlinked,
    trackYardGoToSpotClicked,
    trackYardAssetContainerDropped,
    trackYardAssetContainerHooked,
    trackYardAssetVisitDeparted,
    trackYardAssetAssignedToSpot,
    trackYardAssetReassignedToSpot,
    trackYardAssetsExported,
    trackReadyForPickupNotificationSent,
    trackReadyForPickupNotificationSkipped,
    trackYardTaskCreated,
    trackYardTaskUpdated
  };
}
